/**
 * @file: PreInspectionInfo.tsx
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { StatusLabel } from '@euler/app/components/StatusLabel';
import { IconProps } from '@euler/app/components/icons/types';
import { useSvgProps } from '@euler/app/components/icons/util';
import { Typography } from '@euler/app/components/typography';
import { MemberChatButton } from '@euler/app/flows/chat/components/MemberChatButton';
import type { TaskNavParams } from '@euler/app/flows/task/TaskFlow';
import { flowStatusToCommonTaskStatus } from '@euler/app/flows/task/functions';
import { useTaskContext } from '@euler/app/flows/task/functions/TaskContext';
import { useTaskExtraInfo } from '@euler/app/flows/task/functions/useTaskExtraInfo';
import { TaskCardTitle } from '@euler/app/flows/task/pages/detail/components/TaskCardTitle';
import { TaskDetailSection } from '@euler/app/flows/task/pages/detail/components/TaskDetailSection';
import { TimeElapsedLabel } from '@euler/app/flows/task/pages/detail/components/TimeElapsedLabel';
import { usePreInspectionActions } from '@euler/app/flows/task/pages/pre-inspection/functions/usePreInspectionActions';
import { Avatar } from '@euler/components/Avatar';
import { Center } from '@euler/components/Center';
import { TableView } from '@euler/components/TableView';
import { Img } from '@euler/components/image/AdvancedImage';
import { useAppLoading } from '@euler/components/loading/Provider';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { VehicleInspectionFlow } from '@euler/model';
import {
  CommonTaskStatus,
  InspectionTaskStatus,
  InspectionTemplateSceneType,
} from '@euler/model/enum';
import { timeUtilNow } from '@euler/utils/formatters';
import { usePersistFn } from '@euler/utils/hooks';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { FC, memo } from 'react';
import { View } from 'react-native';
import { Path, Svg } from 'react-native-svg';

export const WiredPreInspectionInfo = memo(
  ({ alwaysVisible }: { alwaysVisible?: boolean }) => {
    const { taskManager, detail } = useTaskContext();
    const extraInfo = useTaskExtraInfo();
    const { rpx } = useResponsiveContext();
    const serviceAgentName = detail.basicInfo.serviceAgentName;
    const preInspection = detail.preInspection;
    const appLoading = useAppLoading();
    const navigation = useNavigation<StackNavigationProp<TaskNavParams>>();
    const { onViewReport } = usePreInspectionActions({ strict: false });

    const onContinueInspection = usePersistFn(async () => {
      if (preInspection.status === CommonTaskStatus.Pending) {
        await appLoading.showWithAction(async () => {
          await taskManager.preInspectionManager.beginPreInspection();
        });
      }
      navigation.navigate('preInspection', {});
    });

    if (
      (!detail.basicInfo.isEntryOrder && !alwaysVisible) ||
      (detail.basicInfo.status === InspectionTaskStatus.Finished &&
        preInspection.status !== CommonTaskStatus.Finished)
    ) {
      return null;
    }

    return (
      <TaskDetailSection
        title={
          <TaskCardTitle
            text="接车预检"
            status={preInspection.status}
            shared={extraInfo?.reportShareSummary?.preInspection}
            viewed={extraInfo?.reportViewSummary?.preInspection}
          />
        }
        headerRight={
          preInspection.status === CommonTaskStatus.Finished
            ? '查看报告'
            : preInspection.status === CommonTaskStatus.InProgress
            ? '预览报告'
            : '继续检测'
        }
        headerRightArrow
        onHeaderRightPress={
          preInspection.status === CommonTaskStatus.Pending
            ? onContinueInspection
            : onViewReport
        }
        bodyStyle={{
          paddingLeft: 0,
          paddingRight: 0,
          paddingTop: 0,
          paddingBottom: 0,
        }}
      >
        <TableView>
          {preInspection.flows.length > 0 ? (
            preInspection.flows.map(flow => (
              <TableView.Item
                key={flow.id}
                onPress={
                  preInspection.status !== CommonTaskStatus.Finished
                    ? onContinueInspection
                    : () => onViewReport()
                }
                title={flow.name}
                titleStyle={{ marginBottom: rpx(5) }}
                titleTextStyle={{
                  fontSize: rpx(17),
                  lineHeight: rpx(20),
                }}
                note={
                  <View>
                    <Typography.Label
                      size={rpx(13)}
                      lineHeight={rpx(18)}
                      color={p => p.components.table.item.noteColor}
                      numberOfLines={1}
                      light
                    >
                      {flowDescription(flow)}
                    </Typography.Label>
                    <View
                      css={`
                        margin-top: ${rpx(4)}px;
                        flex-direction: row;
                        align-items: center;
                      `}
                    >
                      <Avatar
                        name={flow.assignedTo.name}
                        css={`
                          width: ${rpx(16)}px;
                          height: ${rpx(16)}px;
                          border-radius: ${rpx(8)}px;
                          margin-right: ${rpx(3)}px;
                        `}
                      />
                      <Typography.Label
                        size={rpx(12)}
                        regular
                        color={p => p.components.table.item.noteColor}
                      >
                        {`${flow.assignedTo.name}`}
                      </Typography.Label>
                      <MemberChatButton
                        orgMemberId={flow.assignedTo.id}
                        size={rpx(14)}
                        style={{ marginLeft: rpx(4) }}
                      />
                      <Typography.Label
                        size={rpx(12)}
                        color={p => p.components.table.item.noteColor}
                        opacity={0.5}
                        ml={rpx(5)}
                        light
                      >
                        {`${timeUtilNow(flow.createdAt)}`}
                      </Typography.Label>
                    </View>
                  </View>
                }
                noteStyle={{ fontSize: rpx(12) }}
                iconStyle={{ marginRight: rpx(10) }}
                detailIcon="disclosure"
                contentContainerStyle={{
                  paddingTop: rpx(15),
                  paddingBottom: rpx(15),
                }}
                icon={
                  flow.template.icon ? (
                    <Img
                      uri={flow.template.icon}
                      css={`
                        width: ${rpx(32)}px;
                        height: ${rpx(32)}px;
                        border-radius: ${rpx(8)}px;
                      `}
                    />
                  ) : (
                    <Center
                      css={`
                        width: ${rpx(32)}px;
                        height: ${rpx(32)}px;
                      `}
                    >
                      {flow.template.sceneType ===
                      InspectionTemplateSceneType.Dashboard ? (
                        <DashboardIcon />
                      ) : (
                        <FacadeIcon />
                      )}
                    </Center>
                  )
                }
                detail={
                  <View
                    css={`
                      align-items: flex-end;
                      justify-content: center;
                      margin-left: ${rpx(15)}px;
                    `}
                  >
                    <StatusLabel
                      status={flowStatusToCommonTaskStatus(flow.status)}
                      continuable
                    />
                    <TimeElapsedLabel flow={flow} />
                  </View>
                }
              />
            ))
          ) : (
            <TableView.Item
              onPress={
                preInspection.status !== CommonTaskStatus.Finished
                  ? onContinueInspection
                  : () => onViewReport()
              }
              title={
                preInspection.status === CommonTaskStatus.Finished
                  ? `检测技师: ${preInspection.finishedBy ?? ''}`
                  : `服务顾问: ${serviceAgentName}`
              }
              detailIcon="disclosure"
              contentContainerStyle={{
                paddingTop: rpx(10),
                paddingBottom: rpx(10),
              }}
              detail={
                <>
                  {preInspection.status === CommonTaskStatus.Finished ? (
                    <Typography.Label light size={rpx(13)} color="#555">
                      {timeUtilNow(preInspection.finishedAt ?? new Date())}
                    </Typography.Label>
                  ) : null}
                  <StatusLabel
                    status={preInspection.status}
                    continuable
                    css={`
                      margin-left: ${rpx(5)}px;
                    `}
                  />
                </>
              }
            />
          )}
        </TableView>
      </TaskDetailSection>
    );
  },
);

const kDefaultIconSize = 24;

const DashboardIcon: FC<IconProps> = memo(props => {
  const { fill = '#5BA0E0', ...svgProps } = useSvgProps(
    props,
    kDefaultIconSize,
    kDefaultIconSize,
  );
  return (
    <Svg viewBox="0 0 1024 1024" fill="none" {...svgProps}>
      <Path
        d="M861.696 929H148.303l-8.992-10.727c-35.233-42.026-62.722-89.296-81.702-140.493C37.962 724.782 28 669.006 28 612c0-64.382 12.616-126.853 37.498-185.679 24.026-56.804 58.416-107.813 102.212-151.611 43.797-43.797 94.807-78.187 151.611-102.213C378.147 147.616 440.619 135 505 135s126.853 12.616 185.679 37.497c56.805 24.026 107.814 58.416 151.611 102.213s78.187 94.807 102.213 151.611C969.384 485.147 982 547.618 982 612c0 57.006-9.962 112.782-29.609 165.78-18.979 51.197-46.468 98.467-81.701 140.493L861.696 929z m-685.097-60h656.803C890.632 795.912 922 705.196 922 612c0-229.935-187.065-417-417-417S88 382.065 88 612c0 93.196 31.368 183.912 88.599 257z"
        fill={fill}
      />
      <Path
        d="M540 326.432c0 6.941-6.716 12.568-15 12.568h-40c-8.284 0-15-5.626-15-12.568v-98.864c0-6.941 6.716-12.568 15-12.568h40c8.284 0 15 5.626 15 12.568v98.864zM884.433 583.389c6.941 0 12.567 6.716 12.567 15v40c0 8.284-5.626 15-12.567 15h-98.864c-6.941 0-12.568-6.716-12.568-15v-40c0-8.284 5.627-15 12.568-15h98.864zM226.433 653.389c6.941 0 12.567-6.716 12.567-15v-40c0-8.284-5.626-15-12.567-15h-98.864c-6.941 0-12.568 6.716-12.568 15v40c0 8.284 5.627 15 12.568 15h98.864zM248.722 325.557c-5.316-4.462-13.944-2.933-19.269 3.413l-25.711 30.641c-5.325 6.346-5.332 15.108-0.015 19.569l75.734 63.55c5.318 4.461 13.945 2.932 19.27-3.413l25.712-30.642c5.324-6.346 5.331-15.108 0.014-19.57l-75.735-63.548zM738.722 448.443c-5.316 4.462-13.944 2.933-19.269-3.413l-25.711-30.641c-5.326-6.346-5.332-15.108-0.016-19.569l75.734-63.55c5.318-4.461 13.945-2.932 19.27 3.413l25.713 30.642c5.324 6.346 5.33 15.108 0.014 19.57l-75.735 63.548zM626.267 470.737l40.812 47.994-147.293 125.252-40.812-47.994z"
        fill={fill}
      />
      <Path
        d="M469.09 580.496c-20.924 17.795-23.461 49.193-5.667 70.117s49.192 23.462 70.117 5.667c20.924-17.795 23.461-49.193 5.666-70.117s-49.191-23.462-70.116-5.667z"
        fill={fill}
      />
    </Svg>
  );
});

const FacadeIcon: FC<IconProps> = memo(props => {
  const { fill = '#1296DB', ...svgProps } = useSvgProps(
    props,
    kDefaultIconSize,
    kDefaultIconSize,
  );
  return (
    <Svg viewBox="0 0 1024 1024" fill="none" {...svgProps}>
      <Path
        d="M967.153277 488.803734l-891.941475-48.210915 50.624077 423.470809s8.03495 67.496742 56.244649 67.496742h752.124102s4.82024-7.232185 11.250876-19.28461l-500.612897-8.033733c-4.822673 0-8.03495-4.019908-8.03495-8.033734 0-4.823889 4.019908-8.033734 8.837715-8.033733l508.649064 8.033733c22.496887-44.195874 55.441884-124.552671 55.441884-204.103054v-65.889995c0-0.806414 3.219575-61.877385-42.583045-137.41151zM294.580774 596.478875l-101.24937-6.426987C169.224122 588.446358 150.742277 567.555001 152.352673 543.447719c1.60553-23.304517 20.891356-40.979947 43.388243-40.979948h2.413161l101.248154 5.620573c24.103633 1.611612 42.585478 22.501752 40.979947 46.610251-0.802765 24.910048-21.694122 43.387027-45.801404 41.78028z m528.735222-245.883334c-193.654943-120.533979-364.01145-197.674851-364.01145-197.674851S276.900479 73.370307 13.336849 98.281571l57.05228 314.990472 877.477105 47.40815c-27.324425-36.16214-67.497958-74.731359-124.550238-110.084652zM197.351312 394.791415c-52.229607-4.019908-57.855045-28.929955-57.855045-28.929956l-46.610251-197.67485c-15.264702-64.283248 164.729853-28.92509 306.153973 0C540.466541 197.116564 697.961776 331.309715 697.961776 331.309715l8.036167 90.800043c-0.805198 0.001216-456.417024-23.304517-508.646631-27.318343z m532.755129 27.319559l-3.218358-75.535341s89.194512 57.053496 120.536412 81.963544l-117.318054-6.428203z m0 0"
        fill={fill}
      />
      <Path
        d="M245.562228 527.372954l-44.195874-3.213494h-1.60553c-12.857623 0-23.305734 9.641697-24.108499 22.496887-0.802765 13.665253 8.84258 24.911264 22.501752 25.715245l44.195874 3.217143c13.660388 0.802765 24.911264-8.837715 25.714029-22.501752 0.802765-13.659172-8.84258-24.905182-22.501752-25.714029z m0 0"
        fill={fill}
      />
    </Svg>
  );
});

const flowDescription = (flow: VehicleInspectionFlow) => {
  const description = flow.description?.trim();
  if (flow.template.sceneType === InspectionTemplateSceneType.Dashboard) {
    if (description && description !== '系统默认仪表检测模板') {
      return description;
    }
    return '仪表/里程/油量等基础状态检查和记录';
  }

  if (description && description !== '系统默认外观检测模板') {
    return description;
  }

  return '外观/内饰/物品及随车工具等检查和记录';
};
